import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CommentOutlined,
  SendOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { liveChatApi, show422Error, ValidationError } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { formatChatTime } from './liveChatMeta';

/** تفاوت‌های نمایشی هر پیام‌رسان؛ API هر دو یکسان است (livechat/{platform}-bots) */
const PLATFORMS = {
  bale: {
    title: 'بازوهای بله',
    icon: <CommentOutlined className="text-emerald-500" />,
    color: '#10b981',
    bot: 'بازو',
    label: 'بله',
    initial: 'ب',
    empty: 'بازویی متصل نشده است',
    addTitle: 'افزودن بازوی بله',
    guide: 'در بله به «@botfather» پیام دهید، بازو بسازید و توکن آن را اینجا وارد کنید. مشتریان باید به همین بازو پیام بدهند.',
    webhookPorts: '443 یا 88',
  },
  telegram: {
    title: 'ربات‌های تلگرام',
    icon: <SendOutlined className="text-sky-500" />,
    color: '#229ed9',
    bot: 'ربات',
    label: 'تلگرام',
    initial: 'ت',
    empty: 'رباتی متصل نشده است',
    addTitle: 'افزودن ربات تلگرام',
    guide: 'در تلگرام به «@BotFather» پیام دهید، با /newbot ربات بسازید و توکن آن را اینجا وارد کنید. مشتریان باید به همین ربات پیام بدهند.',
    webhookPorts: '443، 80، 88 یا 8443',
  },
};

const DELIVERY_OPTIONS = [
  { value: 'webhook', label: <span className="custom-font">وب‌هوک (لحظه‌ای)</span> },
  { value: 'polling', label: <span className="custom-font">دریافت دوره‌ای</span> },
];

/** وضعیت اتصال یک ربات بر اساس getWebhookInfo و روش دریافت */
const BotStatus = ({ bot }) => {
  const st = bot.status || {};
  if (!bot.is_active) return <Tag className="custom-font">غیرفعال</Tag>;
  if (st.ok === false) {
    return (
      <Tooltip title={st.error}>
        <Tag color="red" icon={<WarningOutlined />} className="custom-font">خطای اتصال</Tag>
      </Tooltip>
    );
  }
  if (bot.delivery === 'webhook') {
    if (!st.webhook_registered) return <Tag color="orange" className="custom-font">وب‌هوک ثبت نشده</Tag>;
    if (st.last_error) {
      return (
        <Tooltip title={st.last_error}>
          <Tag color="orange" icon={<WarningOutlined />} className="custom-font">وب‌هوک با خطا</Tag>
        </Tooltip>
      );
    }
    return <Tag color="green" icon={<CheckCircleOutlined />} className="custom-font">وب‌هوک فعال</Tag>;
  }
  if (st.webhook_conflict) return <Tag color="orange" className="custom-font">وب‌هوک قدیمی فعال است</Tag>;
  return (
    <Tooltip title={bot.last_polled_at ? `آخرین دریافت: ${formatChatTime(bot.last_polled_at)}` : 'هنوز دریافتی انجام نشده'}>
      <Tag color={bot.last_polled_at ? 'green' : 'default'} icon={bot.last_polled_at ? <CheckCircleOutlined /> : null} className="custom-font">
        دریافت دوره‌ای
      </Tag>
    </Tooltip>
  );
};

/**
 * مدیریت ربات‌های پیام‌رسان شرکت (بازوهای بله، ربات‌های تلگرام). توکن فقط نوشتنی است و دوباره نمایش داده نمی‌شود.
 */
const MessengerBotsCard = ({ platform = 'bale', proxyAvailable = false }) => {
  const meta = PLATFORMS[platform];
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [form] = Form.useForm();
  const flowEnabled = Form.useWatch('flow_enabled', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await liveChatApi.getBots(platform);
      setBots(res?.data ?? []);
    } catch (error) {
      message.error(error?.message || `خطا در دریافت ${meta.title}`);
    } finally {
      setLoading(false);
    }
  }, [platform, meta.title]);

  useEffect(() => {
    load();
  }, [load]);

  const replaceBot = (bot) => setBots((prev) => prev.map((b) => (b.id === bot.id ? bot : b)));

  const openModal = (bot = null) => {
    setModal({ open: true, editing: bot });
    form.resetFields();
    form.setFieldsValue(
      bot
        ? {
            name: bot.name,
            delivery: bot.delivery,
            use_proxy: Boolean(bot.use_proxy),
            flow_enabled: bot.flow_enabled,
            welcome_message: bot.welcome_message,
            department_prompt: bot.department_prompt,
            selected_message: bot.selected_message,
            department_ids: bot.department_ids || [],
          }
        : { delivery: undefined, use_proxy: false, flow_enabled: false, department_ids: [] },
    );
    liveChatApi
      .getDepartments()
      .then((res) => setDepartments((res?.data ?? []).filter((d) => d.is_active)))
      .catch(() => setDepartments([]));
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        name: values.name || undefined,
        delivery: values.delivery || undefined,
        use_proxy: Boolean(values.use_proxy),
        flow_enabled: Boolean(values.flow_enabled),
        welcome_message: values.welcome_message || null,
        department_prompt: values.department_prompt || null,
        selected_message: values.selected_message || null,
        department_ids: values.department_ids || [],
      };
      if (values.token) payload.token = values.token.trim();
      const res = modal.editing
        ? await liveChatApi.updateBot(platform, modal.editing.id, payload)
        : await liveChatApi.createBot(platform, payload);
      const bot = res?.data?.bot;
      if (bot) {
        if (modal.editing) replaceBot(bot);
        else setBots((prev) => [...prev, bot]);
      }
      if (res?.data?.warning) message.warning(res.data.warning, 6);
      else message.success(modal.editing ? `${meta.bot} بروزرسانی شد` : `${meta.bot} ${meta.label} متصل شد`);
      setModal({ open: false, editing: null });
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || `خطا در ذخیره ${meta.bot}`);
    } finally {
      setSaving(false);
    }
  };

  const quickUpdate = async (bot, payload, successText) => {
    setBusyId(bot.id);
    try {
      const res = await liveChatApi.updateBot(platform, bot.id, payload);
      if (res?.data?.bot) replaceBot(res.data.bot);
      if (res?.data?.warning) message.warning(res.data.warning, 6);
      else if (successText) message.success(successText);
    } catch (error) {
      message.error(error?.message || `خطا در بروزرسانی ${meta.bot}`);
    } finally {
      setBusyId(null);
    }
  };

  const check = async (bot) => {
    setBusyId(bot.id);
    try {
      const res = await liveChatApi.checkBot(platform, bot.id);
      if (res?.data) replaceBot(res.data);
    } catch (error) {
      message.error(error?.message || 'خطا در بررسی اتصال');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (bot) => {
    setBusyId(bot.id);
    try {
      await liveChatApi.removeBot(platform, bot.id);
      setBots((prev) => prev.filter((b) => b.id !== bot.id));
      message.success(`${meta.bot} حذف شد`);
    } catch (error) {
      message.error(error?.message || `خطا در حذف ${meta.bot}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="livechat-panel p-4 mb-6">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="font-bold text-gray-800 dark:text-gray-100 custom-font flex items-center gap-2">
            {meta.icon} {meta.title}
          </div>
          <div className="text-xs text-gray-500 custom-font mt-1">
            پیام‌هایی که مشتریان به {meta.bot} شرکت در {meta.label} می‌فرستند (متن، عکس، فایل و صوت) در صندوق گفتگو نمایش داده می‌شود و پاسخ اپراتور از طرف {meta.bot} ارسال می‌شود.
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ fontFamily: 'CustomFont' }}>
            بروزرسانی
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} className={PANEL_PRIMARY_BTN_CLASS} style={{ fontFamily: 'CustomFont' }}>
            افزودن {meta.bot}
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {bots.length === 0 ? (
          <Empty className="py-6" description={<span className="custom-font">{meta.empty}</span>} />
        ) : (
          <div className="flex flex-col gap-2">
            {bots.map((bot) => (
              <div key={bot.id} className="rounded-lg border border-primary-100 dark:border-white/10 bg-white dark:bg-white/5 p-3 flex items-center gap-3 flex-wrap">
                <Avatar style={{ backgroundColor: meta.color }}>{(bot.name || meta.initial).charAt(0)}</Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-medium custom-font text-gray-800 dark:text-gray-100 flex items-center gap-2 flex-wrap">
                    {bot.name}
                    {bot.bot_username && (
                      <Typography.Text className="text-xs text-gray-500" style={{ direction: 'ltr' }} copyable={{ text: `@${bot.bot_username}` }}>
                        @{bot.bot_username}
                      </Typography.Text>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 custom-font flex items-center gap-2 flex-wrap mt-1">
                    <BotStatus bot={bot} />
                    {bot.use_proxy && (
                      <Tag icon={<GlobalOutlined />} color="blue" className="custom-font">
                        پراکسی
                      </Tag>
                    )}
                    {bot.status?.pending_updates > 0 && <span>{bot.status.pending_updates.toLocaleString('fa-IR')} پیام در صف</span>}
                    {bot.last_inbound_at && <span>آخرین پیام: {formatChatTime(bot.last_inbound_at)}</span>}
                  </div>
                </div>
                <Space wrap>
                  <Segmented
                    size="small"
                    value={bot.delivery}
                    options={DELIVERY_OPTIONS}
                    disabled={busyId === bot.id}
                    onChange={(v) => quickUpdate(bot, { delivery: v }, 'روش دریافت تغییر کرد')}
                  />
                  <Tooltip title={proxyAvailable ? 'استفاده از پراکسی' : 'پراکسی در تب «عمومی» تعریف نشده است'}>
                    <Switch
                      size="small"
                      checkedChildren={<GlobalOutlined />}
                      unCheckedChildren={<GlobalOutlined />}
                      checked={Boolean(bot.use_proxy)}
                      disabled={!proxyAvailable && !bot.use_proxy}
                      loading={busyId === bot.id}
                      onChange={(v) => quickUpdate(bot, { use_proxy: v }, v ? 'پراکسی فعال شد' : 'پراکسی غیرفعال شد')}
                    />
                  </Tooltip>
                  <Tooltip title={bot.is_active ? 'فعال' : 'غیرفعال'}>
                    <Switch size="small" checked={bot.is_active} loading={busyId === bot.id} onChange={(v) => quickUpdate(bot, { is_active: v })} />
                  </Tooltip>
                  <Tooltip title="بررسی اتصال">
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => check(bot)} loading={busyId === bot.id} />
                  </Tooltip>
                  <Tooltip title="ویرایش / توکن جدید">
                    <Button size="small" icon={<KeyOutlined />} onClick={() => openModal(bot)} />
                  </Tooltip>
                  <Popconfirm
                    title={`این ${meta.bot} جدا شود؟`}
                    description={`وب‌هوک ${meta.bot} در ${meta.label} حذف می‌شود.`}
                    okText="بله"
                    cancelText="خیر"
                    onConfirm={() => remove(bot)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              </div>
            ))}
          </div>
        )}
      </Spin>

      <Modal
        title={modal.editing ? `ویرایش ${modal.editing.name}` : meta.addTitle}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={submit}
        confirmLoading={saving}
        destroyOnHidden
        okText={modal.editing ? 'ذخیره' : 'اتصال'}
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        {!modal.editing && (
          <Alert
            type="info"
            showIcon
            className="custom-font mb-4"
            title={meta.guide}
          />
        )}
        <Form form={form} layout="vertical" className="custom-font">
          <Form.Item
            name="token"
            label={`توکن ${meta.bot}`}
            rules={modal.editing ? [] : [{ required: true, message: `توکن ${meta.bot} را وارد کنید` }]}
            extra={modal.editing ? `خالی = بدون تغییر. توکن جدید باید متعلق به همین ${meta.bot} باشد.` : 'به‌صورت رمزنگاری‌شده ذخیره می‌شود و دوباره نمایش داده نمی‌شود.'}
          >
            <Input.Password dir="ltr" placeholder="123456789:AbCdEf..." autoComplete="new-password" maxLength={200} />
          </Form.Item>
          <Form.Item name="name" label="نام نمایشی (اختیاری)">
            <Input maxLength={150} placeholder={`پیش‌فرض: نام ${meta.bot} در ${meta.label}`} />
          </Form.Item>
          <Form.Item
            name="delivery"
            label="روش دریافت پیام"
            extra={`وب‌هوک لحظه‌ای است ولی به آدرس HTTPS روی پورت ${meta.webhookPorts} نیاز دارد. دریافت دوره‌ای همه‌جا کار می‌کند (هر چند ثانیه).`}
          >
            <Segmented options={DELIVERY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="use_proxy"
            valuePropName="checked"
            label="استفاده از پراکسی"
            extra={
              proxyAvailable
                ? `همه درخواست‌های ${meta.bot}، از جمله بررسی توکن هنگام اتصال، ارسال، دریافت و دانلود فایل، از پراکسی تنظیمات گفتگوی آنلاین عبور می‌کند.`
                : 'پراکسی تعریف نشده است؛ ابتدا در تب «عمومی» آدرس پراکسی ربات‌ها را وارد و ذخیره کنید.'
            }
          >
            <Switch disabled={!proxyAvailable} />
          </Form.Item>

          <div className="livechat-panel p-3 mt-2">
            <div className="font-bold text-sm text-gray-800 dark:text-gray-100 mb-2">شروع گفتگو</div>
            <Form.Item name="welcome_message" label="پیام خوش‌آمد" extra={`اولین پیامی که مشتری پس از پیام دادن به ${meta.bot} دریافت می‌کند.`}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} placeholder="سلام! به پشتیبانی آنلاین خوش آمدید 🌱" />
            </Form.Item>
            <Form.Item name="flow_enabled" valuePropName="checked" label="انتخاب بخش قبل از شروع گفتگو" className="mb-2">
              <Switch />
            </Form.Item>
            {flowEnabled && (
              <>
                {departments.length === 0 && (
                  <Alert type="warning" showIcon className="custom-font mb-3" title="ابتدا در تب «بخش‌ها» حداقل یک بخش فعال (مثلاً پشتیبانی و فروش) تعریف کنید." />
                )}
                <Form.Item name="department_prompt" label="متن درخواست انتخاب">
                  <Input maxLength={300} placeholder="لطفاً بخش مورد نظر خود را انتخاب کنید:" />
                </Form.Item>
                <Form.Item name="department_ids" label="بخش‌های قابل انتخاب" extra="خالی = همه بخش‌های فعال (به ترتیب تعریف‌شده)">
                  <Select mode="multiple" allowClear options={departments.map((d) => ({ value: d.id, label: d.name }))} />
                </Form.Item>
                <Form.Item
                  name="selected_message"
                  label="پیام پس از انتخاب"
                  extra="{department} با نام بخش جایگزین می‌شود. هر بخش می‌تواند پیام اختصاصی خودش را داشته باشد."
                >
                  <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    maxLength={1000}
                    placeholder="شما به بخش «{department}» متصل شدید. پیام خود را بنویسید تا همکاران ما پاسخ دهند."
                  />
                </Form.Item>
              </>
            )}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default MessengerBotsCard;
